import React, { useState, useEffect } from "react";
import classNames from "classnames";
import { Link, useHistory } from "react-router-dom";

import "./RequestDetails.scss";
import { Typography } from "../../../../../components/__common/Typography/Typography";
import {
  RequestStatus,
  RequestType,
  SummaryRequestDetails,
  SummarySubRequestDetails,
} from "../../../../../types/mydata";
import { UtilsDateTime } from "../../../../../utils/UtilsDateTIme";
import { REQUEST_STATUS } from "../../../../../constants/status";
import { Col, Container, Row } from "../../../../../components/Grid/Grid";
import { Image } from "../../../../../components/__common/Image";
import { useTranslation } from "react-i18next";
import GoogleAnalyticsV2 from "../../../../../utils/GoogleAnalyticsV2";
import { GA4EventName } from "../../../../../types/analytics";
import {
  EVENT_REQUEST_DETAILS,
  EVENT_ACTION_SOURCE_SUMMARY,
  EVENT_ACTION_SOURCE_HISTORY,
} from "../../../../../constants/analytics";
import RequestStatusBadge from "../../../../../components/__common/RequestStatusBadge/RequestStatusBadge";

interface Props {
  request: SummaryRequestDetails<SummarySubRequestDetails>;
  route: string;
  isFromSummary?: boolean;
}

export const RequestDetails: React.FC<Props> = (props) => {
  const { t } = useTranslation();

  const { request, route, isFromSummary = false } = props;

  const history = useHistory();

  const [requestFirstThree, setRequestFirstThree] =
    useState<SummarySubRequestDetails[]>();
  const [requestNextThree, setRequestNextThree] =
    useState<SummarySubRequestDetails[]>();
  const [requestRemaining, setRequestRemaining] =
    useState<SummarySubRequestDetails[]>();

  // Map Request Type Title
  const REQUEST_TYPE_LABEL_MAP: Record<RequestType, string> = {
    [RequestType.DOWNLOAD]: "PRIVACY_MYDATA_SUMMARY_DOWNLOAD",
    [RequestType.DELETE]: "PRIVACY_MYDATA_SUMMARY_DELETE",
    [RequestType.UNSUBSCRIBE]: "PRIVACY_MYDATA_SUMMARY_OPTOUT",
  };

  useEffect(() => {
    if (request.sub_request_count > 0) {
      setRequestFirstThree(request.sub_request_list.slice(0, 3));
      setRequestNextThree(request.sub_request_list.slice(3, 6));
      setRequestRemaining(
        request.sub_request_list.slice(6, request.sub_request_list.length)
      );
    }
  }, [request]);

  const handleClickRequest = (requestNumber: string) => {
    GoogleAnalyticsV2.trackEvent(GA4EventName.RequestDetailsAction, {
      action: EVENT_REQUEST_DETAILS,
      source: isFromSummary
        ? EVENT_ACTION_SOURCE_SUMMARY
        : EVENT_ACTION_SOURCE_HISTORY,
    });

    // Track history_download when user clicks a request row from the history page
    if (!isFromSummary) {
      GoogleAnalyticsV2.trackEvent(GA4EventName.HistoryDownload);
    }

    history.push(`${route}/${requestNumber}`);
  };

  return (
    <>
      <Typography
        className="mydata-request-detail__services-container__timestamp"
        variant="body3"
      >
        {UtilsDateTime.convertDateString(request.user_request_date)}
      </Typography>
      <div className="mydata-request-detail__services-container__title">
        <Typography
          variant="body1"
          weight="w700"
          component="span"
          className="mydata-request-detail__services-container__title__type"
        >
          {t(
            REQUEST_TYPE_LABEL_MAP[request.request_type as RequestType] ??
              "PRIVACY_MYDATA_SUMMARY_OPTOUT"
          )}
        </Typography>
        <RequestStatusBadge type={request.request_status as REQUEST_STATUS} />
      </div>
      <Link
        title={t("PRIVACY_MYDATA_VIEW_REQUEST_ALT_TEXT")}
        to="#"
        className={classNames(
          "mydata-request-detail__services-container__list",
          {
            faded:
              request.request_status === RequestStatus.EXPIRED ||
              request.request_status === RequestStatus.CANCELED,
          }
        )}
        onClick={() => handleClickRequest(request.request_number || "")}
      >
        <Container>
          <Row>
            {requestFirstThree?.map((request) => {
              return (
                <Col md={6} sm={12} key={request.sub_request_number}>
                  <Image
                    image={request.service_icon}
                    width={40}
                    height={40}
                    className="service-icon"
                  />
                  <Typography
                    variant="body1"
                    className="service-name"
                    component="span"
                  >
                    {request.service_name}
                  </Typography>
                </Col>
              );
            })}
            {requestNextThree?.length === 1 &&
              requestNextThree.map((subRequest) => {
                return (
                  <Col md={6} sm={12} key={subRequest.service_code}>
                    <Image
                      image={subRequest.service_icon}
                      width={40}
                      height={40}
                      className="service-icon"
                    />
                    <Typography
                      variant="body1"
                      className="service-name"
                      component="span"
                    >
                      {subRequest.service_name}
                    </Typography>
                  </Col>
                );
              })}
            {requestNextThree && requestNextThree.length > 1 && (
              <Col md={6} sm={12} className="more-services">
                {requestNextThree.map((subRequest) => {
                  return (
                    <Image
                      key={subRequest.service_code}
                      image={subRequest.service_icon}
                      width={40}
                      height={40}
                      className="service-icon"
                    />
                  );
                })}

                {requestRemaining && requestRemaining.length > 0 && (
                  <Typography
                    variant="body1"
                    className="service-name"
                    component="span"
                  >
                    + {requestRemaining.length} more
                  </Typography>
                )}
              </Col>
            )}
          </Row>
        </Container>
      </Link>
    </>
  );
};
